// 5MB cap for document / attachment uploads.
//
// Keep in sync with MAX_DOCUMENT_BYTES in Xale-Server/src/utils/s3Uploader.ts —
// the server (presign validation, multer proxy limit and the post-upload HEAD
// re-check) is the authoritative gate; this drives the client-side guard and
// the "reduce & upload" UX. Only images (JPG/PNG) and PDFs can be reduced
// automatically — every other oversized file is reject-only.

export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
export const MAX_UPLOAD_SIZE_LABEL = "5MB";

export function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)}KB`;
  return `${bytes}B`;
}

const IMAGE_MIME_TYPES = ["image/jpeg", "image/jpg", "image/png"];
const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png"];

function fileExtension(name: string): string {
  return (name.split(".").pop() || "").toLowerCase();
}

export function isCompressibleImage(file: File): boolean {
  return (
    IMAGE_MIME_TYPES.includes(file.type) ||
    IMAGE_EXTENSIONS.includes(fileExtension(file.name))
  );
}

export function isCompressiblePdf(file: File): boolean {
  return file.type === "application/pdf" || fileExtension(file.name) === "pdf";
}

/** Only images and PDFs can be auto-reduced; everything else is reject-only. */
export function isCompressibleFile(file: File): boolean {
  return isCompressibleImage(file) || isCompressiblePdf(file);
}

/**
 * Re-encode an image as JPEG at progressively smaller scale/quality until it
 * fits. Transparent PNGs are flattened onto white (JPEG has no alpha).
 */
async function compressImage(file: File, maxBytes: number): Promise<File | null> {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();

    const attempts = [
      { scale: 1, quality: 0.82 },
      { scale: 0.85, quality: 0.75 },
      { scale: 0.7, quality: 0.7 },
      { scale: 0.55, quality: 0.65 },
      { scale: 0.4, quality: 0.6 },
      { scale: 0.3, quality: 0.5 },
      { scale: 0.2, quality: 0.45 },
    ];

    for (const { scale, quality } of attempts) {
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
      canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
      const ctx = canvas.getContext("2d");
      if (!ctx) return null;
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", quality)
      );
      if (blob && blob.size > 0 && blob.size <= maxBytes) {
        const name = `${file.name.replace(/\.(png|jpe?g)$/i, "")}.jpg`;
        return new File([blob], name, { type: "image/jpeg" });
      }
    }
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Rasterise every PDF page to JPEG and rebuild the document, stepping the
 * render resolution/quality down until the result fits. The output keeps the
 * original page sizes but the text is no longer selectable.
 */
async function compressPdf(file: File, maxBytes: number): Promise<File | null> {
  // Heavy deps — loaded only when a PDF actually needs reducing.
  const [pdfjs, worker, { jsPDF }] = await Promise.all([
    import("pdfjs-dist"),
    import("pdfjs-dist/build/pdf.worker.min.mjs?url"),
    import("jspdf"),
  ]);
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default;

  const data = await file.arrayBuffer();
  const loadingTask = pdfjs.getDocument({ data });
  const pdf = await loadingTask.promise;

  const attempts = [
    { renderScale: 1.5, quality: 0.7 },
    { renderScale: 1.2, quality: 0.6 },
    { renderScale: 1.0, quality: 0.5 },
    { renderScale: 0.8, quality: 0.4 },
  ];

  try {
    for (const { renderScale, quality } of attempts) {
      let doc: InstanceType<typeof jsPDF> | null = null;

      for (let pageNo = 1; pageNo <= pdf.numPages; pageNo++) {
        const page = await pdf.getPage(pageNo);
        const baseViewport = page.getViewport({ scale: 1 });
        const viewport = page.getViewport({ scale: renderScale });

        const canvas = document.createElement("canvas");
        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);
        const ctx = canvas.getContext("2d");
        if (!ctx) return null;
        await page.render({ canvas, canvasContext: ctx, viewport }).promise;

        const imgData = canvas.toDataURL("image/jpeg", quality);
        const orientation =
          baseViewport.width > baseViewport.height ? "l" : "p";
        const format: [number, number] = [
          baseViewport.width,
          baseViewport.height,
        ];
        if (!doc) {
          doc = new jsPDF({ orientation, unit: "pt", format, compress: true });
        } else {
          doc.addPage(format, orientation);
        }
        doc.addImage(
          imgData,
          "JPEG",
          0,
          0,
          baseViewport.width,
          baseViewport.height
        );
      }

      if (!doc) return null;
      const blob = doc.output("blob");
      if (blob.size > 0 && blob.size <= maxBytes) {
        return new File([blob], file.name, { type: "application/pdf" });
      }
    }
    return null;
  } finally {
    await loadingTask.destroy().catch(() => undefined);
  }
}

/**
 * Reduce an oversized image/PDF to fit the upload cap. Returns the reduced
 * File, or null when the file type isn't reducible or even the smallest
 * attempt still doesn't fit.
 */
export async function reduceFileToLimit(
  file: File,
  maxBytes: number = MAX_UPLOAD_BYTES
): Promise<File | null> {
  try {
    if (isCompressibleImage(file)) return await compressImage(file, maxBytes);
    if (isCompressiblePdf(file)) return await compressPdf(file, maxBytes);
    return null;
  } catch (error) {
    console.error("File size reduction failed:", error);
    return null;
  }
}
